import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import { canMutateOrders, isOwner } from "../../utils/roles";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import {
  fetchPrintDashboard,
  completePrintBatch,
  discardEmptyBatch,
  removePrintQueueItem,
  removeOrderFromPrint,
  removeItemAndUpdateOrderStatus,
  movePrintItemToSlot,
  placePrintItemInSlot,
  refreshPrintBatch,
  startPrintSession,
  closePrintSession,
  fetchPrintedItems,
  reprintPrintItem,
  resetBatchCounter,
  fetchAllPendingLabels,
  fetchBatchView,
  swapPrintItems,
  replacePrintDesignImage,
  reservePrintItemReturn,
  switchPrintItemToNew,
} from "../../api/PrintQueueAPIs";
import { deleteReturn, fetchAvailableReturnMatches } from "../../api/ReturnsAPIs";
import { CaseStyleBadge, PersonalizedBadge } from "../../Components/orders/CaseStyleBadge";
import { isBlackEdgeCaseStyle } from "../../utils/orderLineDisplay";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronLeft,
  ChevronRight,
  Printer,
  RefreshCw,
  CheckCircle2,
  Plus,
  LayoutGrid,
  Package,
  Clock,
  Layers,
  Tag,
  RotateCcw,
  Loader2,
  ArrowLeftRight,
  X,
  Hash,
  FileDown,
  MoreVertical,
  Trash2,
  ImagePlus,
} from "lucide-react";
import qz from "qz-tray";
import { createLabelPrinterConfig, printOrderPieceLabels } from "../../utils/qzTray";
import {
  buildBatchSlotLineGroups,
  buildPendingLabelLines,
  buildTsplFromLines,
  downloadLabelsPdf,
} from "../../utils/labelPdf";
import { generateCurrentBatchPdf } from "../../api/PrintSheetAPIs";
import CustomPrintBoardModal from "./CustomPrintBoardModal";
import ProductionAnalyticsSection from "./ProductionAnalyticsSection";

const MACHINE_COLUMNS = 6;
const MACHINE_ROWS = 2;
const SLOT_COUNT = MACHINE_COLUMNS * MACHINE_ROWS;

const STATUS_OPTIONS = [
  { value: "Changes", label: "Changes" },
  { value: "Hold", label: "Hold" },
  { value: "Incomplete", label: "Incomplete" },
  { value: "Confirmed", label: "Confirmed" },
  { value: "Old", label: "Old" },
  { value: "Spam", label: "Spam" },
];

const NOTE_NOT_REQUIRED = new Set(["Old", "Confirmed"]);

const itemReturnId = (item) => item?.returnId || item?.reservedReturnId;
const isUsingReturn = (item) =>
  String(item?.printSource || "").toLowerCase() === "return" && Number(itemReturnId(item)) > 0;
const returnMatchKey = (designId, typeId, caseStyleId) =>
  `${Number(designId) || 0}:${Number(typeId) || 0}:${Number(caseStyleId) || 0}`;

const StatCard = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 p-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm text-slate-500 mb-1">{label}</p>
        <p className={`text-3xl font-bold ${accent}`}>{value}</p>
      </div>
      <div className="p-2.5 rounded-xl bg-slate-100 text-slate-600">
        <Icon size={22} />
      </div>
    </div>
  </div>
);

const productionDesignLabel = (item) => {
  if (!item) return "";
  if (item.personalizationLabel) return item.personalizationLabel;
  const names = [item.customNameAr, item.customNameEn].filter(Boolean).join(" - ");
  if (names) return `${item.designName || "Custom Design"} - ${names}`;
  return "";
};

const pieceHasName = (item) =>
  Boolean(item?.isPersonalized)
  || Boolean(String(item?.customNameAr || "").trim())
  || Boolean(String(item?.customNameEn || "").trim());

const EmptySlotCard = ({
  machinePosition,
  swapSourceId,
  onEmptyClick,
  isLoading,
  canInteract,
}) => {
  const isMoveTarget = swapSourceId != null && canInteract;

  return (
    <button
      type="button"
      disabled={isLoading || !canInteract}
      onClick={() => onEmptyClick(machinePosition)}
      className={`relative aspect-[1/1.6] w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition ${
        isMoveTarget
          ? "border-amber-400 bg-amber-50/80 text-amber-800 ring-2 ring-amber-200 cursor-pointer hover:bg-amber-100"
          : canInteract
            ? "border-slate-200 bg-slate-50/80 text-slate-400 hover:border-indigo-300 hover:bg-indigo-50/40 cursor-pointer"
            : "border-slate-200 bg-slate-50/80 text-slate-400 cursor-default opacity-80"
      } disabled:opacity-50`}
      title={
        isMoveTarget
          ? `نقل إلى الموضع #${machinePosition}`
          : canInteract
            ? `وضع قطعة في الموضع #${machinePosition}`
            : `موضع فارغ #${machinePosition}`
      }
    >
      <div
        className="absolute top-2 right-2 z-10 bg-slate-900/70 text-white text-[17px] font-bold px-2.5 py-1 rounded-lg"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        #{machinePosition}
      </div>
      <LayoutGrid size={36} className="mb-2 opacity-40" />
      <span className="text-base font-medium">فارغ</span>
      {isMoveTarget && (
        <span className="mt-1 text-xs font-semibold text-amber-700">انقل هنا</span>
      )}
    </button>
  );
};

const PrintItemCard = ({
  item,
  machinePosition,
  isLoading,
  canMutate,
  swapSourceId,
  onSwapClick,
  onEmptyClick,
  onRemovePiece,
  onRemoveOrder,
  onOpenStatusDialog,
  onReplaceDesign,
  openMenuId,
  setOpenMenuId,
  matchGroup,
  onOpenReturnDetails,
  onUseReturn,
  onUseNew,
}) => {
  const menuRef = useRef(null);
  const menuOpen = openMenuId === item?.printQueueItemId;

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen, setOpenMenuId]);

  if (!item) {
    return (
      <EmptySlotCard
        machinePosition={machinePosition}
        swapSourceId={swapSourceId}
        onEmptyClick={onEmptyClick}
        isLoading={isLoading}
        canInteract={canMutate}
      />
    );
  }

  const imageSrc = item.designImageUrl
    ? `${API_BASE_URL_V2}${item.designImageUrl}`
    : null;

  const isSwapSource = swapSourceId === item.printQueueItemId;
  const isSwapTargetMode = swapSourceId != null && !isSwapSource;
  const usingReturn = isUsingReturn(item);
  const matchCount = matchGroup?.matchCount || 0;
  const hasAvailableReturn = !usingReturn && matchCount > 0;
  const hasName = pieceHasName(item);
  const blackEdges = isBlackEdgeCaseStyle(item.caseStyleName);

  return (
    <div
      className={`group relative aspect-[1/1.6] rounded-xl border shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col ${
        isSwapSource
          ? "border-amber-400 ring-2 ring-amber-300 bg-white"
          : isSwapTargetMode
            ? "border-indigo-300 ring-1 ring-indigo-200 cursor-pointer bg-white"
            : usingReturn
              ? "border-sky-500 ring-2 ring-sky-200 bg-sky-50"
              : hasAvailableReturn
                ? "border-blue-400 ring-2 ring-blue-100 bg-blue-50"
                : blackEdges
                  ? "border-zinc-900 ring-2 ring-zinc-700 bg-zinc-50"
                  : hasName
                    ? "border-fuchsia-500 ring-2 ring-fuchsia-300 bg-fuchsia-50"
                    : "border-slate-200 bg-white"
      }`}
      onClick={() => {
        if (isSwapTargetMode) onSwapClick(item);
      }}
    >
      <div
        className="absolute top-2 right-2 z-10 bg-slate-900/85 text-white text-[17px] font-bold px-2.5 py-1 rounded-lg"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        #{item.machinePosition}
      </div>
      <div
        className="absolute top-2 left-2 z-10 bg-indigo-600 text-white text-sm font-bold px-2 py-1 rounded"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        Q{item.queuePosition}
      </div>
      {item.hasCustomPrintImage && (
        <div
          className="absolute top-10 right-2 z-10 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded"
          title="تم استبدال صورة الطباعة"
        >
          معدّل
        </div>
      )}
      {(usingReturn || hasAvailableReturn) && (
        <button
          type="button"
          className={`absolute top-16 right-2 z-10 text-[10px] font-bold px-2 py-1 rounded shadow ${
            usingReturn ? "bg-sky-700 text-white" : "bg-blue-600 text-white"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenReturnDetails?.(item, matchGroup);
          }}
          title={usingReturn ? "يتم استخدام مرتجع" : `${matchCount} مرتجعات متاحة`}
        >
          {usingReturn
            ? "RETURN"
            : matchCount > 1
              ? `${matchCount} مرتجعات متاحة`
              : "مرتجع متاح"}
        </button>
      )}

      <button
        type="button"
        disabled={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          onSwapClick(item);
        }}
        className={`absolute top-10 left-2 z-10 p-1.5 rounded-lg shadow transition disabled:opacity-50 ${
          isSwapSource
            ? "bg-amber-500 text-white"
            : "bg-white/95 text-indigo-700 hover:bg-indigo-600 hover:text-white opacity-90 group-hover:opacity-100"
        }`}
        title={
          isSwapSource
            ? "إلغاء التبديل"
            : swapSourceId
              ? "تبديل مع هذا"
              : "تبديل / نقل المكان"
        }
      >
        <ArrowLeftRight size={14} />
      </button>

      {blackEdges ? (
        <div className="pointer-events-none absolute inset-0 z-[6] rounded-xl ring-4 ring-inset ring-zinc-900" />
      ) : hasName ? (
        <div className="pointer-events-none absolute inset-0 z-[6] rounded-xl ring-4 ring-inset ring-fuchsia-500" />
      ) : null}
      {hasName && (
        <div className="absolute bottom-24 right-2 z-10 bg-fuchsia-600 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow">
          باسم
        </div>
      )}

      <div className="h-[82%] bg-slate-100 relative flex items-center justify-center overflow-hidden">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={item.designCode || "design"}
            className="w-full h-full object-contain scale-125"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm px-2 text-center font-medium">
            {item.isMissingDesign ? "صورة غير متوفرة" : "لا توجد صورة"}
          </div>
        )}
      </div>

      <div
        className={`shrink-0 p-2 text-left border-t space-y-0 ${
          blackEdges
            ? "border-zinc-300 bg-zinc-100"
            : hasName
              ? "border-fuchsia-200 bg-fuchsia-50"
              : "border-slate-100 bg-white"
        }`}
        dir="ltr"
        style={{ fontFamily: "Arial, sans-serif" }}
      >
        <p
          className="m-0 flex gap-1 text-sm font-semibold text-slate-800 truncate"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          <span>Order:</span>
          <span className="truncate">{item.itemLabel}</span>
        </p>

        <p
          className="m-0 flex gap-1 text-sm font-semibold text-indigo-600 truncate"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          <span>Code:</span>
          <span className="truncate">{item.designCode || "—"}</span>
        </p>
        {productionDesignLabel(item) ? (
          <p
            className="m-0 flex gap-1 text-[11px] font-semibold text-purple-700 truncate"
            style={{ fontFamily: "Arial, sans-serif" }}
            title={productionDesignLabel(item)}
          >
            <span>Name:</span>
            <span className="truncate">{productionDesignLabel(item)}</span>
          </p>
        ) : null}

        <p
          className="m-0 flex gap-1 text-[11px] font-semibold text-slate-800 truncate"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          <span>Case Type:</span>
          <span className="truncate">{item.caseTypeName || item.mainTypeName || (item.mainTypeId ? "—" : item.typeName) || "نوع غير معروف"}</span>
        </p>
        <p
          className="m-0 flex gap-1 text-[11px] font-semibold text-slate-600 truncate"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          <span>Mobile:</span>
          <span className="truncate">{item.mobileTypeName || item.typeName || "نوع غير معروف"}</span>
        </p>
        <div className="mt-1 flex flex-wrap gap-1">
          <CaseStyleBadge
            name={item.caseStyleName}
            usesTypeStock={item.caseStyleUsesTypeStock !== false}
            textClass="text-[13px]"
          />
        </div>

        <p
          className="m-0 flex gap-1 text-sm font-semibold text-indigo-600"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          <span>Piece:</span>
          <span>
            {item.itemIndexInOrder} / {item.totalItemsInOrder}
          </span>
        </p>
        <p
          className={`m-0 mt-0.5 inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${
            usingReturn ? "bg-sky-700 text-white" : "bg-slate-800 text-white"
          } ${canMutate && (usingReturn || hasAvailableReturn) ? "cursor-pointer" : ""}`}
          onClick={(e) => {
            if (!canMutate || !(usingReturn || hasAvailableReturn)) return;
            e.stopPropagation();
            onOpenReturnDetails?.(item, matchGroup);
          }}
          title={usingReturn || hasAvailableReturn ? "عرض حالة المرتجع قبل الاختيار" : undefined}
        >
          {usingReturn ? "RETURN" : "NEW PRINT"}
        </p>
        {canMutate && (usingReturn || hasAvailableReturn) && (
          <div className="mt-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
            {hasAvailableReturn && !usingReturn && (
              <>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => onOpenReturnDetails?.(item, matchGroup)}
                  className="flex-1 text-[10px] font-bold px-1 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  استخدام المرتجع
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => onUseNew?.(item, matchGroup)}
                  className="flex-1 text-[10px] font-bold px-1 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  استخدام جديد
                </button>
              </>
            )}
            {usingReturn && (
              <>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => onOpenReturnDetails?.(item, matchGroup)}
                  className="flex-1 text-[10px] font-bold px-1 py-1 rounded bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100 disabled:opacity-50"
                >
                  تفاصيل
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => onUseNew?.(item, matchGroup)}
                  className="flex-1 text-[10px] font-bold px-1 py-1 rounded bg-white border border-sky-300 text-sky-800 hover:bg-sky-50 disabled:opacity-50"
                >
                  استخدام جديد
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {canMutate && (
        <div
          ref={menuRef}
          className="absolute bottom-[22%] right-3 z-20"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            disabled={isLoading}
            onClick={() =>
              setOpenMenuId(menuOpen ? null : item.printQueueItemId)
            }
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 p-2 rounded-lg shadow disabled:opacity-50"
            title="المزيد"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <MoreVertical size={18} />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute bottom-full right-0 mb-2 w-64 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden py-1"
            >
              <button
                type="button"
                role="menuitem"
                disabled={isLoading}
                onClick={() => {
                  setOpenMenuId(null);
                  onReplaceDesign(item);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 transition text-right disabled:opacity-40"
              >
                <ImagePlus size={15} className="text-emerald-500 shrink-0" />
                تعديل التصميم
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={isLoading}
                onClick={() => {
                  setOpenMenuId(null);
                  onRemovePiece(item.printQueueItemId);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-800 transition text-right disabled:opacity-40"
              >
                <Trash2 size={15} className="text-red-500 shrink-0" />
                مسح القطعه دي من الطباعه
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={isLoading}
                onClick={() => {
                  setOpenMenuId(null);
                  onRemoveOrder(item.orderId);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-orange-800 transition text-right disabled:opacity-40"
              >
                <Package size={15} className="text-orange-500 shrink-0" />
                مسح الاوردر كله من الطباعه
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={isLoading}
                onClick={() => {
                  setOpenMenuId(null);
                  onOpenStatusDialog(item);
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-800 transition text-right disabled:opacity-40"
              >
                <CheckCircle2 size={15} className="text-indigo-500 shrink-0" />
                يمسحه من الطباعه و يحدث حاله الاوردر
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PreviewItem = ({ item, index }) => (
  <div className={`flex items-center gap-3 p-2 rounded-lg border ${
    pieceHasName(item) ? "bg-fuchsia-50 border-fuchsia-300" : "bg-white/60 border-slate-100"
  }`}>
    <span className="text-xs font-bold text-slate-400 w-6">{index + 1}</span>
    <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden shrink-0">
      {item.designImageUrl ? (
        <img
          src={`${API_BASE_URL_V2}${item.designImageUrl}`}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : null}
    </div>
    <div className="flex-1 min-w-0 text-right">
      <p className="text-sm font-semibold text-slate-700 truncate">
        {item.itemLabel}
        {pieceHasName(item) ? (
          <span className="mr-2 inline-flex rounded bg-fuchsia-600 px-1.5 py-0.5 text-[10px] text-white">باسم</span>
        ) : null}
      </p>
      <p className="text-xs text-slate-500 truncate">{item.designCode}</p>
      {productionDesignLabel(item) ? (
        <p className="text-[11px] text-purple-700 truncate">{productionDesignLabel(item)}</p>
      ) : null}
    </div>
  </div>
);

const ProductionDashboardPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [batchView, setBatchView] = useState(null);
  const [batchOffset, setBatchOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isPrintingAll, setIsPrintingAll] = useState(false);
  const [isPrintingOrderPieces, setIsPrintingOrderPieces] = useState(false);
  const [printedItems, setPrintedItems] = useState([]);
  const [printedPage, setPrintedPage] = useState(0);
  const [printedHasMore, setPrintedHasMore] = useState(false);
  const [printedTotal, setPrintedTotal] = useState(0);
  const [isLoadingPrinted, setIsLoadingPrinted] = useState(false);
  const [isLoadingMorePrinted, setIsLoadingMorePrinted] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  const [swapSourceId, setSwapSourceId] = useState(null);
  const [isGeneratingBatchPdf, setIsGeneratingBatchPdf] = useState(false);
  const [isDownloadingBatchLabelsPdf, setIsDownloadingBatchLabelsPdf] = useState(false);
  const [isDownloadingAllLabelsPdf, setIsDownloadingAllLabelsPdf] = useState(false);
  const [showCustomPrintBoard, setShowCustomPrintBoard] = useState(false);
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [openHeaderMenu, setOpenHeaderMenu] = useState(null);
  const headerMenusRef = useRef(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [placeDialog, setPlaceDialog] = useState(null);
  const [placeForm, setPlaceForm] = useState({
    designCode: "",
    orderId: "",
    itemIndexInOrder: "1",
    totalItemsInOrder: "1",
  });
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", note: "" });
  const [returnMatches, setReturnMatches] = useState({});
  const [returnDialog, setReturnDialog] = useState(null);
  const designFileInputRef = useRef(null);
  const pendingDesignReplaceIdRef = useRef(null);

  const loadPrintedItems = useCallback(async (pageNum = 0, append = false) => {
    if (append) setIsLoadingMorePrinted(true);
    else setIsLoadingPrinted(true);
    try {
      const data = await fetchPrintedItems(pageNum, 12);
      setPrintedItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
      setPrintedPage(pageNum);
      setPrintedHasMore(!!data.hasMore);
      setPrintedTotal(data.totalCount || 0);
    } catch (err) {
      toast.error(err.message || "فشل تحميل القطع المطبوعة");
    } finally {
      setIsLoadingPrinted(false);
      setIsLoadingMorePrinted(false);
    }
  }, []);

  const loadReturnMatchesForSlots = useCallback(async (slots) => {
    const keys = [];
    const seen = new Set();
    for (const item of slots || []) {
      const designId = Number(item?.designId);
      const typeId = Number(item?.typeId);
      if (!designId || !typeId) continue;
      const caseStyleId = Number(item?.caseStyleId) || 0;
      const key = returnMatchKey(designId, typeId, caseStyleId);
      if (seen.has(key)) continue;
      seen.add(key);
      keys.push({
        designId,
        typeId,
        caseStyleId: caseStyleId || null,
      });
    }
    if (keys.length === 0) {
      setReturnMatches({});
      return;
    }
    try {
      const groups = await fetchAvailableReturnMatches(keys);
      const map = {};
      for (const group of groups || []) {
        const designId = Number(group.designId);
        const typeId = Number(group.typeId);
        if (!designId || !typeId) continue;
        map[returnMatchKey(designId, typeId, group.caseStyleId)] = group;
      }
      setReturnMatches(map);
    } catch (err) {
      toast.error(err.message || "فشل تحميل المرتجعات المتاحة");
    }
  }, []);

  const loadBatchView = useCallback(async (offset = 0) => {
    try {
      const view = await fetchBatchView(offset);
      setBatchView(view);
      setBatchOffset(view.batchOffset ?? offset);
      await loadReturnMatchesForSlots(view?.slots);
      return view;
    } catch (err) {
      toast.error(err.message || "فشل تحميل عرض الدفعة");
      return null;
    }
  }, [loadReturnMatchesForSlots]);

  const loadDashboard = useCallback(async (offset = 0) => {
    try {
      const data = await fetchPrintDashboard();
      setDashboard(data);
      if (data?.session) {
        await loadBatchView(offset);
      } else {
        setBatchView(null);
        setBatchOffset(0);
        setReturnMatches({});
      }
    } catch (err) {
      toast.error(err.message || "فشل تحميل لوحة الإنتاج");
    } finally {
      setIsLoading(false);
    }
  }, [loadBatchView]);

  useEffect(() => {
    if (!canMutateOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadDashboard(0);
    loadPrintedItems(0);
  }, [authState, navigate, loadDashboard, loadPrintedItems]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setSwapSourceId(null);
        setOpenHeaderMenu(null);
        setOpenMenuId(null);
        setPlaceDialog(null);
        setStatusDialog(null);
        setReturnDialog(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!openHeaderMenu) return;
    const onPointerDown = (e) => {
      if (headerMenusRef.current && !headerMenusRef.current.contains(e.target)) {
        setOpenHeaderMenu(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [openHeaderMenu]);

  const handleUseReturn = async (item, returnId = null) => {
    if (!item?.printQueueItemId) return;
    setActionLoading(true);
    try {
      const data = await reservePrintItemReturn(item.printQueueItemId, returnId || null);
      setDashboard(data);
      await loadBatchView(batchOffset);
      setReturnDialog(null);
      toast.success("تم حجز المرتجع لهذه القطعة");
    } catch (err) {
      toast.error(err.message || "فشل استخدام المرتجع");
      await loadBatchView(batchOffset);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUseNew = async (item, matchGroup = null) => {
    if (!item?.printQueueItemId) return;

    const reservedId = Number(itemReturnId(item)) > 0 ? itemReturnId(item) : null;
    const availableId = matchGroup?.returns?.[0]?.returnId || null;
    const returnId = reservedId || availableId;

    const deleteReturnToo = !!returnId && window.confirm(
      "هل القطعة المرتجعة مش موجودة أو فيها مشكلة وعايز تمسحها برضو؟"
    );

    setActionLoading(true);
    try {
      if (reservedId) {
        const data = await switchPrintItemToNew(item.printQueueItemId);
        setDashboard(data);
      }
      if (deleteReturnToo && returnId) {
        await deleteReturn(returnId);
        toast.success("سيتم طباعة قطعة جديدة وتم مسح المرتجع");
      } else {
        toast.success("سيتم طباعة قطعة جديدة");
      }
      await loadBatchView(batchOffset);
      setReturnDialog(null);
    } catch (err) {
      toast.error(err.message || "فشل التحويل للطباعة الجديدة");
      await loadBatchView(batchOffset);
    } finally {
      setActionLoading(false);
    }
  };

  const runAction = async (action, successMessage) => {
    setActionLoading(true);
    try {
      const data = await action();
      setDashboard(data);
      await loadBatchView(batchOffset);
      if (successMessage) toast.success(successMessage);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "حدث خطأ");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNavigateBatch = async (nextOffset) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await loadBatchView(nextOffset);
      // Keep swapSourceId so user can pick the second item on another batch
      setOpenMenuId(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwapClick = async (item) => {
    if (!item?.printQueueItemId || actionLoading) return;

    if (!swapSourceId) {
      setSwapSourceId(item.printQueueItemId);
      toast.info("اختر جراب ثاني للتبديل (ممكن في دفعة تانية) أو موضع فارغ للنقل");
      return;
    }

    if (swapSourceId === item.printQueueItemId) {
      setSwapSourceId(null);
      return;
    }

    setActionLoading(true);
    try {
      const data = await swapPrintItems(swapSourceId, item.printQueueItemId);
      setDashboard(data);
      setSwapSourceId(null);
      await loadBatchView(batchOffset);
      toast.success("تم تبديل المكانين");
    } catch (err) {
      toast.error(err.message || "فشل التبديل");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmptySlotClick = async (machinePosition) => {
    if (actionLoading) return;

    if (swapSourceId) {
      setActionLoading(true);
      try {
        const data = await movePrintItemToSlot(
          swapSourceId,
          machinePosition,
          batchView?.printBatchId ?? null
        );
        setDashboard(data);
        setSwapSourceId(null);
        await loadBatchView(batchOffset);
        toast.success(`تم النقل إلى الموضع #${machinePosition}`);
        await loadPrintedItems(0);
      } catch (err) {
        toast.error(err.message || "فشل النقل");
      } finally {
        setActionLoading(false);
      }
      return;
    }

    setPlaceForm({
      designCode: "",
      orderId: "",
      itemIndexInOrder: "1",
      totalItemsInOrder: "1",
    });
    setPlaceDialog({ machinePosition });
  };

  const handlePlaceSubmit = async (e) => {
    e?.preventDefault?.();
    if (!placeDialog?.machinePosition || actionLoading) return;

    const designCode = placeForm.designCode.trim().toUpperCase();
    const orderId = Number(placeForm.orderId);
    const itemIndexInOrder = Number(placeForm.itemIndexInOrder);
    const totalItemsInOrder = Number(placeForm.totalItemsInOrder);

    if (!designCode) {
      toast.warning("كود التصميم مطلوب");
      return;
    }
    if (!Number.isFinite(orderId) || orderId <= 0) {
      toast.warning("رقم الأوردر مطلوب");
      return;
    }
    if (!Number.isFinite(itemIndexInOrder) || itemIndexInOrder <= 0) {
      toast.warning("رقم القطعة مطلوب");
      return;
    }
    if (!Number.isFinite(totalItemsInOrder) || totalItemsInOrder <= 0) {
      toast.warning("إجمالي القطع مطلوب");
      return;
    }
    if (itemIndexInOrder > totalItemsInOrder) {
      toast.warning("رقم القطعة لا يمكن أن يتجاوز الإجمالي");
      return;
    }

    setActionLoading(true);
    try {
      const data = await placePrintItemInSlot({
        machinePosition: placeDialog.machinePosition,
        designCode,
        orderId,
        itemIndexInOrder,
        totalItemsInOrder,
        printBatchId: batchView?.printBatchId ?? null,
      });
      setDashboard(data);
      setPlaceDialog(null);
      await loadBatchView(batchOffset);
      toast.success(`تم وضع القطعة في الموضع #${placeDialog.machinePosition}`);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "فشل وضع القطعة");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteBatch = () => {
    if (!batchView?.isCurrentBatch) {
      toast.warning("إكمال الدفعة متاح للدفعة الحالية فقط — ارجع للدفعة الأولى");
      return;
    }
    const isEmpty = (batchView?.filledCount ?? 0) === 0;
    const message = isEmpty
      ? "الدفعة فاضية تمامًا. إكمالها هيمسحها وينتقل للدفعة التالية؟"
      : "تأكيد إكمال الدفعة الحالية؟ سيتم وضع علامة مطبوع على جميع العناصر.";
    if (!window.confirm(message)) {
      return;
    }
    runAction(async () => {
      const data = await completePrintBatch();
      if (!isEmpty) setActivityRefresh((n) => n + 1);
      return data;
    }, isEmpty ? "تم تخطي الدفعة الفاضية" : "تم إكمال الدفعة بنجاح");
  };

  const handleDiscardEmptyBatch = () => {
    const printBatchId = batchView?.printBatchId;
    if (!printBatchId) {
      toast.warning("لا توجد دفعة لمسحها");
      return;
    }
    if ((batchView?.filledCount ?? 0) > 0) {
      toast.warning("يمكن مسح الدفعة الفاضية فقط");
      return;
    }
    if (
      !window.confirm(
        "مسح هذه الدفعة الفاضية؟ هتتشال من الطابور لأنها مش ليها لازمة."
      )
    ) {
      return;
    }
    runAction(() => discardEmptyBatch(printBatchId), "تم مسح الدفعة الفاضية");
  };

  const handleRemovePiece = (itemId) => {
    if (
      !window.confirm(
        "مسح هذه القطعة من الطباعة؟\n\nالموضع هيفضل فاضي ولن يُسحب عنصر تلقائيًا."
      )
    ) {
      return;
    }
    runAction(() => removePrintQueueItem(itemId), "تم مسح القطعة — الموضع فاضي");
  };

  const handleRemoveOrder = (orderId) => {
    if (
      !window.confirm(
        `مسح الأوردر #${orderId} كله من الطباعة؟\n\nكل قطعه Printing/Queued هتتشال، والمواضع هتفضل فاضية.`
      )
    ) {
      return;
    }
    runAction(
      () => removeOrderFromPrint(orderId),
      "تم مسح الأوردر من الطباعة"
    );
  };

  const handleOpenStatusDialog = (item) => {
    setStatusForm({ status: "", note: "" });
    setStatusDialog({
      printQueueItemId: item.printQueueItemId,
      orderId: item.orderId,
      itemLabel: item.itemLabel,
    });
  };

  const handleReplaceDesign = (item) => {
    if (!item?.printQueueItemId || actionLoading) return;
    pendingDesignReplaceIdRef.current = item.printQueueItemId;
    if (designFileInputRef.current) {
      designFileInputRef.current.value = "";
      designFileInputRef.current.click();
    }
  };

  const handleDesignFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const itemId = pendingDesignReplaceIdRef.current;
    e.target.value = "";
    pendingDesignReplaceIdRef.current = null;

    if (!file || !itemId) return;

    const allowed = ["image/png", "image/jpeg", "image/webp", "image/jpg"];
    const extOk = /\.(png|jpe?g|webp)$/i.test(file.name);
    if (!allowed.includes(file.type) && !extOk) {
      toast.warning("الصورة لازم تكون PNG أو JPG أو WEBP");
      return;
    }

    await runAction(
      () => replacePrintDesignImage(itemId, file),
      "تم رفع صورة التصميم — هتُستخدم في الـ PDF"
    );
  };

  const handleStatusSubmit = async (e) => {
    e?.preventDefault?.();
    if (!statusDialog?.printQueueItemId || actionLoading) return;

    const { status, note } = statusForm;
    if (!status) {
      toast.warning("اختر حالة الأوردر");
      return;
    }
    if (!NOTE_NOT_REQUIRED.has(status) && !note.trim()) {
      toast.warning("الملاحظة مطلوبة لهذه الحالة");
      return;
    }

    setActionLoading(true);
    try {
      const data = await removeItemAndUpdateOrderStatus(
        statusDialog.printQueueItemId,
        status,
        note.trim() || null
      );
      setDashboard(data);
      setStatusDialog(null);
      await loadBatchView(batchOffset);
      toast.success(`تم المسح وتحديث الحالة إلى ${status}`);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "فشل تحديث الحالة");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartSession = () => {
    if (
      !window.confirm(
        "بدء جلسة طباعة جديدة؟ سيتم إغلاق الجلسة الحالية إن وجدت.\n\nلن يتم تحرير المرتجعات المحجوزة تلقائياً."
      )
    ) {
      return;
    }
    setBatchOffset(0);
    setSwapSourceId(null);
    runAction(startPrintSession, "تم بدء جلسة جديدة");
  };

  const handleCloseSession = () => {
    if (
      !window.confirm(
        "إغلاق جلسة الطباعة الحالية؟\n\nلن يتم تحرير المرتجعات المحجوزة تلقائياً. أكمل الدفعة أو أزل القطع المحجوزة أولاً."
      )
    ) {
      return;
    }
    setSwapSourceId(null);
    runAction(closePrintSession, "تم إغلاق الجلسة");
  };

  const handleResetBatchCounter = () => {
    if (
      !window.confirm(
        "تصفير عداد الدفعات؟\n\nرقم الدفعة الحالية سيرجع لصفر، وتقدر تعدّ دفعات اليوم من جديد."
      )
    ) {
      return;
    }
    runAction(resetBatchCounter, "تم تصفير عداد الدفعات");
  };

  const handleReprintItem = async (itemId) => {
    if (
      !window.confirm(
        "إعادة طباعة هذه القطعة؟\n\nهتخش في الدفعة الحالية، وآخر جراب في الماكينة هيرجع للطابور للدفعة التالية."
      )
    ) {
      return;
    }

    setReprintingId(itemId);
    try {
      const data = await reprintPrintItem(itemId);
      setDashboard(data);
      toast.success("تم إدخال القطعة للدفعة الحالية");
      await loadBatchView(0);
      await loadPrintedItems(0);
    } catch (err) {
      toast.error(err.message || "فشل إعادة الطباعة");
    } finally {
      setReprintingId(null);
    }
  };

  const printSlotsAsLabels = async (slots, batchNum) => {
    const config = await createLabelPrinterConfig();
    const data = buildBatchSlotLineGroups(slots, batchNum).map((lines) => ({
      type: "raw",
      data: buildTsplFromLines(lines),
    }));
    await qz.print(config, data);
    return data.length;
  };

  const handlePrintBatch = async () => {
    setIsPrinting(true);
    try {
      const slotsToPrint = batchView?.slots || [];
      const batchNum = batchView?.displayBatchNumber ?? 0;
      const count = await printSlotsAsLabels(slotsToPrint, batchNum);
      if (count > 0) {
        toast.success(`تم طباعة ${count} ملصق للدفعة #${batchNum}`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل الطباعة");
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrintAllLabels = async () => {
    setIsPrintingAll(true);
    try {
      const result = await fetchAllPendingLabels();
      const items = result.items || [];

      if (items.length === 0) {
        toast.warning("لا توجد قطع للطباعة");
        return;
      }

      const confirmed = window.confirm(
        `طباعة كل الملصقات؟\n\n${result.totalLabels} ملصق على ${result.totalBatches} دفعة\n(كل ملصق فيه رقم الدفعة)`
      );
      if (!confirmed) return;

      const config = await createLabelPrinterConfig();
      const data = items.map((item) => ({
        type: "raw",
        data: buildTsplFromLines(buildPendingLabelLines(item)),
      }));
      await qz.print(config, data);
      toast.success(`تم طباعة ${data.length} ملصق (${result.totalBatches} دفعة)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل طباعة كل الملصقات");
    } finally {
      setIsPrintingAll(false);
    }
  };

  const handleDownloadBatchLabelsPdf = () => {
    setIsDownloadingBatchLabelsPdf(true);
    try {
      const slotsToPrint = batchView?.slots || [];
      const batchNum = batchView?.displayBatchNumber ?? 0;
      const count = downloadLabelsPdf(
        buildBatchSlotLineGroups(slotsToPrint, batchNum),
        `labels-batch-${batchNum}.pdf`
      );
      toast.success(`تم تحميل PDF ${count} ملصق للدفعة #${batchNum}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل إنشاء PDF الملصقات");
    } finally {
      setIsDownloadingBatchLabelsPdf(false);
    }
  };

  const handleDownloadAllLabelsPdf = async () => {
    setIsDownloadingAllLabelsPdf(true);
    try {
      const result = await fetchAllPendingLabels();
      const items = result.items || [];

      if (items.length === 0) {
        toast.warning("لا توجد قطع للتحميل");
        return;
      }

      const confirmed = window.confirm(
        `تنزيل PDF كل الملصقات؟\n\n${result.totalLabels} ملصق على ${result.totalBatches} دفعة\n(مقاس كل ملصق 55×40 مم)`
      );
      if (!confirmed) return;

      const count = downloadLabelsPdf(
        items.map((item) => buildPendingLabelLines(item)),
        `labels-all-batches-${Date.now()}.pdf`
      );
      toast.success(`تم تحميل PDF ${count} ملصق (${result.totalBatches} دفعة)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل إنشاء PDF كل الملصقات");
    } finally {
      setIsDownloadingAllLabelsPdf(false);
    }
  };

  const handleGenerateCurrentBatchPdf = async () => {
    if (!session) {
      toast.warning("لا توجد جلسة طباعة نشطة");
      return;
    }

    setIsGeneratingBatchPdf(true);
    try {
      const result = await generateCurrentBatchPdf();
      toast.success(`تم تحميل ${result.fileName}`);
    } catch (err) {
      const missing = err.missingDesignCodes?.length
        ? ` — الناقص: ${err.missingDesignCodes.join(", ")}`
        : "";
      toast.error((err.message || "فشل إنشاء PDF الدفعة") + missing);
    } finally {
      setIsGeneratingBatchPdf(false);
    }
  };

  const handlePrintOrderPieceLabels = async () => {
    setIsPrintingOrderPieces(true);
    try {
      const result = await fetchAllPendingLabels();
      const items = result.items || [];

      const byOrder = new Map();
      for (const item of items) {
        if (item?.orderId == null) continue;
        if (!byOrder.has(item.orderId)) {
          byOrder.set(item.orderId, item.totalItemsInOrder || 0);
        }
      }

      // Fallback: unique orders from the currently visible batch
      if (byOrder.size === 0) {
        for (const item of batchView?.slots || []) {
          if (!item?.orderId) continue;
          if (!byOrder.has(item.orderId)) {
            byOrder.set(item.orderId, item.totalItemsInOrder || 0);
          }
        }
      }

      if (byOrder.size === 0) {
        toast.warning("لا توجد طلبات للطباعة");
        return;
      }

      const labels = Array.from(byOrder.entries()).map(([orderNumber, pieces]) => ({
        orderNumber,
        pieces,
      }));

      if (
        !window.confirm(
          `طباعة ملصقات رقم الأوردر + عدد القطع؟\n\n${labels.length} ملصق على ورق 50×25`
        )
      ) {
        return;
      }

      const count = await printOrderPieceLabels(labels);
      toast.success(`تم طباعة ${count} ملصق`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "فشل طباعة ملصقات الأوردرات");
    } finally {
      setIsPrintingOrderPieces(false);
    }
  };

  const session = dashboard?.session;
  const slots = batchView?.slots || Array(SLOT_COUNT).fill(null);
  const filledCount = batchView?.filledCount ?? slots.filter(Boolean).length;
  const batchNumber = batchView?.displayBatchNumber ?? dashboard?.currentBatch?.batchNumber ?? 0;
  const totalPendingBatches = batchView?.totalPendingBatches ?? 0;
  const isCurrentBatch = batchView?.isCurrentBatch !== false;
  const pdfActionsBusy =
    actionLoading ||
    isPrinting ||
    isPrintingAll ||
    isPrintingOrderPieces ||
    isGeneratingBatchPdf ||
    isDownloadingBatchLabelsPdf ||
    isDownloadingAllLabelsPdf;
  const statusNeedsNote =
    statusForm.status && !NOTE_NOT_REQUIRED.has(statusForm.status);

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/40 to-slate-100 p-4 md:p-6"
    >
      <ToastContainer
        position="bottom-left"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnHover={false}
        rtl
        limit={3}
        className="compact-toasts"
      />
      <input
        ref={designFileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={handleDesignFileSelected}
      />
      <CustomPrintBoardModal
        open={showCustomPrintBoard}
        onClose={() => setShowCustomPrintBoard(false)}
      />

      {/* Place item dialog */}
      {placeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div
            dir="rtl"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">
                وضع قطعة في الموضع #{placeDialog.machinePosition}
              </h3>
              <button
                type="button"
                onClick={() => setPlaceDialog(null)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handlePlaceSubmit} className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  كود التصميم *
                </label>
                <input
                  type="text"
                  value={placeForm.designCode}
                  onChange={(e) =>
                    setPlaceForm((f) => ({
                      ...f,
                      designCode: e.target.value.toUpperCase(),
                    }))
                  }
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  dir="ltr"
                  placeholder="مثال: C002"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  رقم الأوردر *
                </label>
                <input
                  type="number"
                  min="1"
                  value={placeForm.orderId}
                  onChange={(e) =>
                    setPlaceForm((f) => ({ ...f, orderId: e.target.value }))
                  }
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                  dir="ltr"
                  placeholder="Order Id"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    رقم القطعة *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={placeForm.itemIndexInOrder}
                    onChange={(e) =>
                      setPlaceForm((f) => ({
                        ...f,
                        itemIndexInOrder: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    إجمالي القطع *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={placeForm.totalItemsInOrder}
                    onChange={(e) =>
                      setPlaceForm((f) => ({
                        ...f,
                        totalItemsInOrder: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPlaceDialog(null)}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 transition"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  وضع القطعة
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Return details dialog */}
      {returnDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div dir="rtl" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-800">تفاصيل المرتجع</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {returnDialog.item?.itemLabel} — {returnDialog.item?.designCode} / Mobile: {returnDialog.item?.mobileTypeName || returnDialog.item?.typeName} / {returnDialog.item?.caseStyleName || "شفاف"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReturnDialog(null)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {isUsingReturn(returnDialog.item) && (
                <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 text-sm space-y-1">
                  <p className="font-bold text-sky-800">يتم استخدام مرتجع محجوز</p>
                  <p>رقم المرتجع: {returnDialog.item?.reservedReturn?.returnId || itemReturnId(returnDialog.item)}</p>
                  <p>تاريخ المرتجع: {returnDialog.item?.reservedReturn?.createdAt
                    ? new Date(returnDialog.item.reservedReturn.createdAt).toLocaleString("ar-EG")
                    : "—"}</p>
                  <p>طلب المرتجع: {returnDialog.item?.reservedReturn?.orderId || "—"}</p>
                  <p className="font-semibold text-rose-800">
                    حالة القطعة: {returnDialog.item?.reservedReturn?.notes || "لا يوجد وصف عيب"}
                  </p>
                </div>
              )}

              {(returnDialog.matchGroup?.matchCount || 0) > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    {returnDialog.matchGroup.matchCount} مرتجعات متاحة — الأقدم أولاً
                  </p>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {(returnDialog.matchGroup.returns || []).map((ret, idx) => (
                      <div
                        key={ret.returnId}
                        className="rounded-xl border border-slate-200 p-3 text-sm flex items-center justify-between gap-3"
                      >
                        <div>
                          <p className="font-semibold">#{ret.returnId}{idx === 0 ? " — الأقدم" : ""}</p>
                          <p className="text-slate-500">
                            {ret.createdAt ? new Date(ret.createdAt).toLocaleString("ar-EG") : "—"}
                            {ret.orderId ? ` — طلب ${ret.orderId}` : ""}
                            {ret.caseStyleName ? ` — ${ret.caseStyleName}` : ""}
                          </p>
                          <p className={`mt-1 font-semibold ${ret.notes ? "text-rose-800" : "text-slate-500"}`}>
                            حالة القطعة: {ret.notes || "لا يوجد وصف عيب"}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={actionLoading}
                          onClick={() => handleUseReturn(returnDialog.item, ret.returnId)}
                          className="shrink-0 px-3 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                        >
                          استخدام المرتجع
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReturnDialog(null)}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-slate-200"
                >
                  إغلاق
                </button>
                {(isUsingReturn(returnDialog.item) || (returnDialog.matchGroup?.matchCount || 0) > 0) && (
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => handleUseNew(returnDialog.item, returnDialog.matchGroup)}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-slate-800 text-white disabled:opacity-50"
                  >
                    استخدام جديد
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status update dialog */}
      {statusDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div
            dir="rtl"
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-800">
                  تحديث حالة الأوردر
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {statusDialog.itemLabel || `#${statusDialog.orderId}`} — يُمسح من الطباعة
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStatusDialog(null)}
                className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleStatusSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      setStatusForm((f) => ({ ...f, status: opt.value }))
                    }
                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold border transition ${
                      statusForm.status === opt.value
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {statusNeedsNote && (
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">
                    ملاحظة *
                  </label>
                  <textarea
                    value={statusForm.note}
                    onChange={(e) =>
                      setStatusForm((f) => ({ ...f, note: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
                    placeholder="اكتب سبب تغيير الحالة..."
                  />
                </div>
              )}

              {!statusNeedsNote && statusForm.status && (
                <p className="text-xs text-slate-500">
                  الملاحظة اختيارية لحالة {statusForm.status}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setStatusDialog(null)}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 transition"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || !statusForm.status}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={14} className="animate-spin" />}
                  تأكيد
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/production/add-orders")}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md transition"
            >
              <Plus size={18} />
              إضافة طلبات للطابور
            </button>
            <button
              onClick={() => setShowCustomPrintBoard(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold shadow-md transition"
              title="لوحة طباعة مخصصة ٢×٦"
            >
              <LayoutGrid size={18} />
              Custom Print PDF
            </button>
            <button
              onClick={handleStartSession}
              disabled={actionLoading}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-medium hover:bg-slate-50 transition disabled:opacity-50"
            >
              جلسة جديدة
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
                <Printer className="text-indigo-600" />
                لوحة الإنتاج — طابور الطباعة
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                تخطيط الماكينة ٦ × ٢ — ١٢ جراب لكل دفعة
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition"
            >
              <ChevronLeft size={26} />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : !session ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
            <Printer size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">لا توجد جلسة طباعة نشطة</h2>
            <p className="text-slate-500 mb-6">
              ابدأ جلسة جديدة أو أضف طلبات إلى طابور الطباعة
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={handleStartSession}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
              >
                بدء جلسة
              </button>
              <button
                onClick={() => navigate("/production/add-orders")}
                className="px-6 py-3 bg-white border border-slate-200 rounded-xl font-semibold hover:bg-slate-50 transition"
              >
                إضافة طلبات
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                label="الجلسة الحالية"
                value={`#${session.printSessionId}`}
                icon={Layers}
                accent="text-indigo-600"
              />
              <StatCard
                label="متبقي للطباعة"
                value={session.remainingCount}
                icon={Clock}
                accent="text-amber-600"
              />
              <StatCard
                label="مطبوع"
                value={session.printedCount}
                icon={CheckCircle2}
                accent="text-emerald-600"
              />
              <StatCard
                label="دفعات اليوم"
                value={session.batchesSinceReset ?? 0}
                icon={Package}
                accent="text-slate-700"
              />
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 p-4 shadow-sm flex flex-col justify-between">
                <p className="text-sm text-slate-500 mb-2">عداد الدفعات</p>
                <button
                  type="button"
                  onClick={handleResetBatchCounter}
                  disabled={actionLoading}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-xl text-sm font-semibold transition disabled:opacity-50"
                >
                  <RotateCcw size={16} />
                  تصفير للـ 0
                </button>
              </div>
            </div>

            {/* Current Batch */}
            <div className="bg-white/90 backdrop-blur rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">
                      {isCurrentBatch ? "الدفعة الحالية" : "دفعة"}{" "}
                      {batchNumber >= 0 ? `#${batchNumber}` : ""}
                    </h2>
                    <p className="text-sm text-slate-500">
                      {filledCount} / 12 موضع — {session.queuedCount} في الانتظار
                      {totalPendingBatches > 0 && (
                        <span className="mr-2 text-indigo-600 font-medium">
                          · دفعة {batchOffset + 1} من {totalPendingBatches}
                        </span>
                      )}
                    </p>
                    {filledCount === 0 && (
                      <p className="text-xs text-amber-700 mt-1">
                        {isCurrentBatch
                          ? "الدفعة فاضية — تقدر تمسحها أو تكملها من قائمة «الدفعة»"
                          : "الدفعة فاضية — تقدر تمسحها من قائمة «الدفعة»"}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2" ref={headerMenusRef}>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenHeaderMenu((open) => (open === "labels" ? null : "labels"))
                        }
                        disabled={pdfActionsBusy}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-semibold transition disabled:opacity-50"
                        title="الملصقات"
                        aria-haspopup="menu"
                        aria-expanded={openHeaderMenu === "labels"}
                      >
                        {isPrinting || isPrintingAll || isPrintingOrderPieces || isDownloadingBatchLabelsPdf || isDownloadingAllLabelsPdf ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Tag size={16} className="text-indigo-600" />
                        )}
                        ملصقات
                        <MoreVertical size={16} />
                      </button>

                      {openHeaderMenu === "labels" && (
                        <div
                          role="menu"
                          className="absolute left-0 top-full mt-2 z-30 w-80 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden py-1"
                        >
                          <button
                            type="button"
                            role="menuitem"
                            disabled={
                              pdfActionsBusy ||
                              ((session?.remainingCount ?? 0) === 0 && filledCount === 0)
                            }
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handlePrintOrderPieceLabels();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <Hash size={16} className="text-teal-600 shrink-0" />
                            {isPrintingOrderPieces ? "جاري الطباعة..." : "طباعة أرقام الأوردرات"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handlePrintBatch();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <Tag size={16} className="text-indigo-600 shrink-0" />
                            {isPrinting ? "جاري الطباعة..." : "طباعة الدفعة"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handleDownloadBatchLabelsPdf();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            {isDownloadingBatchLabelsPdf ? (
                              <Loader2 size={16} className="animate-spin text-indigo-600 shrink-0" />
                            ) : (
                              <FileDown size={16} className="text-indigo-600 shrink-0" />
                            )}
                            {isDownloadingBatchLabelsPdf
                              ? "جاري إنشاء PDF..."
                              : "تنزيل PDF الملصقات لهذه الدفعة"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy || (session?.remainingCount ?? 0) === 0}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handlePrintAllLabels();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-violet-50 hover:text-violet-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <Printer size={16} className="text-violet-600 shrink-0" />
                            {isPrintingAll ? "جاري طباعة الكل..." : "طباعة كل الملصقات"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy || (session?.remainingCount ?? 0) === 0}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handleDownloadAllLabelsPdf();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-violet-50 hover:text-violet-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            {isDownloadingAllLabelsPdf ? (
                              <Loader2 size={16} className="animate-spin text-violet-600 shrink-0" />
                            ) : (
                              <FileDown size={16} className="text-violet-600 shrink-0" />
                            )}
                            {isDownloadingAllLabelsPdf
                              ? "جاري إنشاء PDF..."
                              : "تنزيل PDF الملصقات لكل الدفعات"}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenHeaderMenu((open) => (open === "batch" ? null : "batch"))
                        }
                        disabled={pdfActionsBusy}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-slate-700 text-sm font-semibold transition disabled:opacity-50"
                        title="إجراءات الدفعة"
                        aria-haspopup="menu"
                        aria-expanded={openHeaderMenu === "batch"}
                      >
                        {actionLoading || isGeneratingBatchPdf ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Layers size={16} className="text-slate-600" />
                        )}
                        الدفعة
                        <MoreVertical size={16} />
                      </button>

                      {openHeaderMenu === "batch" && (
                        <div
                          role="menu"
                          className="absolute left-0 top-full mt-2 z-30 w-80 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden py-1"
                        >
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy || !isCurrentBatch}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handleCompleteBatch();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                            {filledCount === 0 ? "إكمال الدفعة الفاضية" : "إكمال الدفعة"}
                          </button>
                          {filledCount === 0 && (
                            <button
                              type="button"
                              role="menuitem"
                              disabled={pdfActionsBusy || !batchView?.printBatchId}
                              onClick={() => {
                                setOpenHeaderMenu(null);
                                handleDiscardEmptyBatch();
                              }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-red-50 hover:text-red-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                            >
                              <Trash2 size={16} className="text-red-500 shrink-0" />
                              مسح الدفعة الفاضية
                            </button>
                          )}
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              runAction(refreshPrintBatch, "تم تحديث الطابور");
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <RefreshCw size={16} className={`text-slate-600 shrink-0 ${actionLoading ? "animate-spin" : ""}`} />
                            تحديث
                          </button>
                          <div className="my-1 border-t border-slate-100" />
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              handleGenerateCurrentBatchPdf();
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-rose-50 hover:text-rose-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            {isGeneratingBatchPdf ? (
                              <Loader2 size={16} className="animate-spin text-rose-600 shrink-0" />
                            ) : (
                              <FileDown size={16} className="text-rose-600 shrink-0" />
                            )}
                            {isGeneratingBatchPdf
                              ? "جاري إنشاء PDF..."
                              : "Generate Current Batch PDF"}
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            disabled={pdfActionsBusy}
                            onClick={() => {
                              setOpenHeaderMenu(null);
                              setShowCustomPrintBoard(true);
                            }}
                            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-orange-800 transition disabled:opacity-40 disabled:cursor-not-allowed text-right"
                          >
                            <LayoutGrid size={16} className="text-orange-600 shrink-0" />
                            Custom Print PDF
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Batch navigation */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    disabled={actionLoading || !batchView?.hasPrevious}
                    onClick={() => handleNavigateBatch(batchOffset - 1)}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title="الدفعة السابقة"
                  >
                    <ChevronRight size={20} />
                    السابقة
                  </button>
                  <div className="px-4 py-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-800 text-sm font-bold min-w-[140px] text-center">
                    {totalPendingBatches > 0
                      ? `${batchOffset + 1} / ${totalPendingBatches}`
                      : "—"}
                  </div>
                  <button
                    type="button"
                    disabled={actionLoading || !batchView?.hasNext}
                    onClick={() => handleNavigateBatch(batchOffset + 1)}
                    className="flex items-center gap-1 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                    title="الدفعة التالية"
                  >
                    التالية
                    <ChevronLeft size={20} />
                  </button>
                </div>

                {swapSourceId && (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                    <span className="flex items-center gap-2 font-medium">
                      <ArrowLeftRight size={16} />
                      اختر جراب تاني للتبديل (حتى في دفعة تانية)، أو موضع فاضي للنقل. Esc للإلغاء
                    </span>
                    <button
                      type="button"
                      onClick={() => setSwapSourceId(null)}
                      className="p-1 rounded-lg hover:bg-amber-100 transition"
                      title="إلغاء"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="p-4 md:p-6">
                <div
                  className="grid gap-3 md:gap-4"
                  style={{
                    gridTemplateColumns: `repeat(${MACHINE_COLUMNS}, minmax(0, 1fr))`,
                  }}
                >
                  {Array.from({ length: SLOT_COUNT }).map((_, index) => (
                    <PrintItemCard
                      key={slots[index]?.printQueueItemId || `empty-${index}`}
                      item={slots[index]}
                      machinePosition={index + 1}
                      isLoading={actionLoading}
                      canMutate={!!session}
                      swapSourceId={swapSourceId}
                      onSwapClick={handleSwapClick}
                      onEmptyClick={handleEmptySlotClick}
                      onRemovePiece={handleRemovePiece}
                      onRemoveOrder={handleRemoveOrder}
                      onOpenStatusDialog={handleOpenStatusDialog}
                      onReplaceDesign={handleReplaceDesign}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      matchGroup={
                        Number(slots[index]?.designId) && Number(slots[index]?.typeId)
                          ? returnMatches[
                              returnMatchKey(
                                slots[index].designId,
                                slots[index].typeId,
                                slots[index].caseStyleId
                              )
                            ]
                          : null
                      }
                      onOpenReturnDetails={(cardItem, group) =>
                        setReturnDialog({ item: cardItem, matchGroup: group })
                      }
                      onUseReturn={handleUseReturn}
                      onUseNew={handleUseNew}
                    />
                  ))}
                </div>

                <div className="mt-4 flex justify-center gap-1 text-[10px] text-slate-400 font-mono">
                  <span>صف ١: ١–٦</span>
                  <span>|</span>
                  <span>صف ٢: ٧–١٢</span>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Upcoming preview */}
              <div className="bg-white/80 rounded-2xl border border-slate-200 p-5 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Clock size={18} className="text-amber-500" />
                  معاينة الطابور التالي
                </h3>
                {dashboard.upcomingPreview?.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {dashboard.upcomingPreview.map((item, i) => (
                      <PreviewItem key={item.printQueueItemId} item={item} index={i} />
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm text-center py-8">لا توجد عناصر في الانتظار</p>
                )}
              </div>

              {/* Printed history */}
              <div className="bg-white/80 rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-emerald-500" />
                    القطع المطبوعة
                  </h3>
                  <span className="text-xs text-slate-500">{printedTotal} قطعة</span>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  اضغط «إعادة طباعة» لإدخال القطعة في الدفعة الحالية (آخر جراب يرجع للطابور التالي)
                </p>

                {isLoadingPrinted ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : printedItems.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-8">لا توجد قطع مطبوعة بعد</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {printedItems.map((item) => (
                      <div
                        key={item.printQueueItemId}
                        className={`flex items-center gap-3 p-2 rounded-lg border ${
                          pieceHasName(item)
                            ? "bg-fuchsia-50 border-fuchsia-300"
                            : "bg-emerald-50/60 border-emerald-100"
                        }`}
                      >
                        <div className="w-12 h-12 rounded-lg bg-white overflow-hidden shrink-0 border border-emerald-100">
                          {item.designImageUrl ? (
                            <img
                              src={`${API_BASE_URL_V2}${item.designImageUrl}`}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0 text-right">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {item.itemLabel}
                            {pieceHasName(item) ? (
                              <span className="mr-2 inline-flex rounded bg-fuchsia-600 px-1.5 py-0.5 text-[10px] text-white">باسم</span>
                            ) : null}
                          </p>
                          <p className="text-xs text-indigo-600 truncate">
                            {item.designCode || "—"} · Case: {item.caseTypeName || item.mainTypeName || (item.mainTypeId ? "—" : item.typeName) || "—"} · Mobile: {item.mobileTypeName || item.typeName || "—"}
                            <span className="mx-1 inline-flex gap-1 align-middle">
                              {item.isPersonalized ? <PersonalizedBadge /> : null}
                              <CaseStyleBadge name={item.caseStyleName} usesTypeStock={item.caseStyleUsesTypeStock !== false} />
                            </span>
                          </p>
                          {productionDesignLabel(item) ? (
                            <p className="text-[11px] text-purple-700 truncate">
                              {productionDesignLabel(item)}
                            </p>
                          ) : null}
                          <p className="text-[11px] text-slate-500">
                            {item.itemIndexInOrder}/{item.totalItemsInOrder}
                            {item.machinePosition ? ` · كان #${item.machinePosition}` : ""}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={actionLoading || reprintingId === item.printQueueItemId}
                          onClick={() => handleReprintItem(item.printQueueItemId)}
                          className="shrink-0 flex items-center gap-1 px-2.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition disabled:opacity-50"
                          title="إعادة طباعة"
                        >
                          {reprintingId === item.printQueueItemId ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RotateCcw size={14} />
                          )}
                          إعادة
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {printedHasMore && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      disabled={isLoadingMorePrinted}
                      onClick={() => loadPrintedItems(printedPage + 1, true)}
                      className="px-5 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition disabled:opacity-50"
                    >
                      {isLoadingMorePrinted ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={14} className="animate-spin" /> جاري التحميل...
                        </span>
                      ) : (
                        "تحميل المزيد"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCloseSession}
                disabled={actionLoading}
                className="text-sm text-slate-500 hover:text-red-600 transition disabled:opacity-50"
              >
                إغلاق الجلسة
              </button>
            </div>
          </>
        )}

        {isOwner(authState.role) && (
          <ProductionAnalyticsSection refreshKey={activityRefresh} />
        )}
      </div>
    </div>
  );
};

export default ProductionDashboardPage;
